/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getPaymentStatus } from '@/lib/telemedicine';

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 8;

const TelemedicinePaymentResultPage = ({ context = 'subscription', outcome = 'success' }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'checking', data: null, error: null, attempts: 0 });

  const sessionId = new URLSearchParams(location.search).get('session_id');

  useEffect(() => {
    if (outcome === 'cancel') {
      setState({ status: 'cancelled', data: null, error: null, attempts: 0 });
      return;
    }
    if (!sessionId) {
      setState({ status: 'error', data: null, error: 'missing_session_id', attempts: 0 });
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === 'paid') {
          setState({ status: 'paid', data, error: null, attempts });
          return;
        }
        if (data.status === 'expired' || data.payment_status === 'expired' || data.payment_status === 'failed') {
          setState({ status: 'failed', data, error: null, attempts });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: 'timeout', data, error: null, attempts });
          return;
        }
        setState({ status: 'checking', data, error: null, attempts });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ status: 'error', data: null, error: e?.response?.data?.detail || 'unknown', attempts });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, outcome]);

  const goBack = () => navigate('/telemedicine');

  const isSubscription = context === 'subscription';
  const isPremium = context === 'premium';

  let icon, title, desc, badgeClass;
  switch (state.status) {
    case 'paid':
      icon = <CheckCircle2 className="h-12 w-12 text-emerald-600" />;
      title = isSubscription ? t('telemedSubActivatedTitle') : (isPremium ? t('premiumActivatedTitle') : t('telemedApptPaidTitle'));
      desc = isSubscription ? t('telemedSubActivatedDesc') : (isPremium ? t('premiumActivatedDesc') : t('telemedApptPaidDesc'));
      badgeClass = 'text-emerald-600';
      break;
    case 'failed':
      icon = <XCircle className="h-12 w-12 text-red-600" />;
      title = t('telemedPaymentFailedTitle');
      desc = t('telemedPaymentFailedDesc');
      badgeClass = 'text-red-600';
      break;
    case 'cancelled':
      icon = <XCircle className="h-12 w-12 text-amber-500" />;
      title = t('telemedPaymentCancelledTitle');
      desc = t('telemedPaymentCancelledDesc');
      badgeClass = 'text-amber-600';
      break;
    case 'timeout':
      icon = <XCircle className="h-12 w-12 text-amber-500" />;
      title = t('telemedPaymentTimeoutTitle');
      desc = t('telemedPaymentTimeoutDesc');
      badgeClass = 'text-amber-600';
      break;
    case 'error':
      icon = <XCircle className="h-12 w-12 text-red-600" />;
      title = t('telemedPaymentErrorTitle');
      desc = state.error || t('telemedPaymentErrorDesc');
      badgeClass = 'text-red-600';
      break;
    default:
      icon = <Loader2 className="h-12 w-12 text-primary animate-spin" />;
      title = t('telemedPaymentCheckingTitle');
      desc = t('telemedPaymentCheckingDesc');
      badgeClass = 'text-primary';
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="payment-result-page">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">{icon}</div>
          <CardTitle className={badgeClass} data-testid="payment-result-title">{title}</CardTitle>
          <CardDescription data-testid="payment-result-desc">{desc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {state.data?.amount && (
            <div className="text-center text-sm text-muted-foreground">
              {t('amount')}: <span className="font-semibold text-foreground">€ {Number(state.data.amount).toFixed(2)}</span>
            </div>
          )}
          <Button onClick={goBack} className="w-full" data-testid="payment-result-back-btn">
            {t('telemedBackToDashboard')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default TelemedicinePaymentResultPage;
